import * as React from "react";
import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  Business,
  KeyboardArrowDown,
  Check,
} from "@mui/icons-material";
import { useCompanyContext } from "../context/CompanyContext";

interface CompanySelectorProps {
  collapsed?: boolean;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ collapsed = false }) => {
  const { companies, activeCompany, setActiveCompany, isLoading } = useCompanyContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isChanging, setIsChanging] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  // Debug logging
  useEffect(() => {
    console.log('🏢 CompanySelector: Render - isLoading:', isLoading, 'companies:', companies.length, 'activeCompany:', activeCompany, 'anchorEl:', !!anchorEl, 'open:', open);
  }, [isLoading, companies, activeCompany, anchorEl, open]);

  // Listen for company change events to show loading state
  useEffect(() => {
    const handleCompanyChange = () => {
      setIsChanging(true);
      // Reset loading state after a short delay
      setTimeout(() => setIsChanging(false), 1000);
    };

    window.addEventListener('companyChanged', handleCompanyChange);
    return () => window.removeEventListener('companyChanged', handleCompanyChange);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    console.log('🏢 CompanySelector: handleClick called, companies.length:', companies.length);
    event.preventDefault();
    event.stopPropagation();
    if (companies.length > 1) {
      // Use ref if available, otherwise use currentTarget
      const anchor = chipRef.current || event.currentTarget;
      console.log('🏢 CompanySelector: Setting anchorEl to:', anchor);
      setAnchorEl(anchor as HTMLElement);
    } else {
      console.log('🏢 CompanySelector: Only 1 company, not opening dropdown');
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectCompany = (companyId: string) => {
    if (companyId !== activeCompany?.companyID) {
      setIsChanging(true);
      setActiveCompany(companyId);
    }
    handleClose();
  };

  if (isLoading || !activeCompany) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {collapsed ? (
          <CircularProgress size={20} sx={{ color: "#86eb85" }} />
        ) : (
          <CircularProgress size={16} sx={{ color: "#86eb85", mr: 1 }} />
        )}
      </Box>
    );
  }

  // Single company - show as chip (non-clickable)
  if (companies.length === 1) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {collapsed ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Business sx={{ color: "#86eb85", fontSize: 24 }} />
          </Box>
        ) : (
          <Chip
            icon={<Business sx={{ color: "#86eb85 !important" }} />}
            label={activeCompany.name}
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              backgroundColor: "rgba(134, 235, 133, 0.1)",
              color: "#86eb85",
              border: "1px solid rgba(134, 235, 133, 0.3)",
              fontWeight: 600,
              fontSize: "13px",
              fontFamily: "Montserrat",
              height: "36px",
              "& .MuiChip-icon": {
                marginLeft: "8px",
              },
              "& .MuiChip-label": {
                paddingLeft: "8px",
                paddingRight: "8px",
              },
            }}
          />
        )}
      </Box>
    );
  }

  // Multiple companies - show as dropdown chip
  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {collapsed ? (
          <Box
            onClick={handleClick}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            {isChanging ? (
              <CircularProgress size={20} sx={{ color: "#86eb85" }} />
            ) : (
              <Business sx={{ color: "#86eb85", fontSize: 24 }} />
            )}
          </Box>
        ) : (
          <Box
            ref={chipRef}
            onClick={handleClick}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "rgba(134, 235, 133, 0.1)",
              color: "#86eb85",
              border: "1px solid rgba(134, 235, 133, 0.3)",
              borderRadius: "16px",
              padding: "6px 12px",
              minHeight: "36px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(134, 235, 133, 0.15)",
                borderColor: "rgba(134, 235, 133, 0.5)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
              <Business sx={{ color: "#86eb85", fontSize: 18 }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  color: "#86eb85",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {activeCompany.name}
              </Typography>
            </Box>
            <KeyboardArrowDown sx={{ color: "#86eb85", fontSize: 20, ml: 1 }} />
          </Box>
        )}
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disablePortal={false} // Render in portal to avoid z-index issues
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            color: "#fff",
            minWidth: 200,
            maxWidth: 280,
            mt: 0.5,
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.3)",
            zIndex: 1300, // Ensure it's above sidebar
            "& .MuiMenuItem-root": {
              fontFamily: "Montserrat",
              fontSize: "14px",
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: "rgba(134, 235, 133, 0.1)",
              },
            },
          },
        }}
        MenuListProps={{
          sx: {
            py: 0.5,
          },
        }}
      >
        {companies.map((company) => (
          <MenuItem
            key={company.companyID}
            onClick={() => handleSelectCompany(company.companyID)}
            selected={company.companyID === activeCompany?.companyID}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "rgba(134, 235, 133, 0.15)",
                "&:hover": {
                  backgroundColor: "rgba(134, 235, 133, 0.2)",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {company.companyID === activeCompany?.companyID ? (
                <Check sx={{ color: "#86eb85", fontSize: 20 }} />
              ) : (
                <Box sx={{ width: 20 }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={company.name}
              primaryTypographyProps={{
                sx: {
                  color: company.companyID === activeCompany?.companyID ? "#86eb85" : "#fff",
                  fontWeight: company.companyID === activeCompany?.companyID ? 600 : 500,
                },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CompanySelector;


